/**
 * @file 閲覧位置 (学習者 × 資料の、最後に開いたページ) のクライアント。
 * @what: `GET /families/{fid}/study/plans/{personId}/reading_positions` (新しい順の一覧) と、
 *   `PUT .../reading_positions/{sourceId}` (1資料ぶんの上書き)。
 * @why: 学習者のデータなので plan 配下に置く。端末の localStorage だけでは学習者に依らず、
 *   他の端末にも届かないので、本だなを学習者ごとに組み立てる手がかりとしてサーバに置く。
 * @failure: ApiError を伝播する。positions が空 = まだ何も開いていない、は正常系である。
 */

import { apiFetch } from "@/shared/api/client";

export interface StudyReadingPosition {
  source_id: string;
  /** リーダーの連番 (1始まり)。 */
  page: number;
  /** 最後に開いた時刻 (サーバの時刻、RFC 3339)。 */
  opened_at: string;
  /**
   * これまでに読んだページ (滞在が15秒以上) の連番 (昇順)。読んだ時間 (api/readingSessions.ts) の
   * 足し込みで進む。古いサーバでは省略される。
   */
  pages_read?: number[];
  /** この資料で実際に操作していた秒数の累計である。古いサーバでは省略される。 */
  active_seconds?: number;
  /** 最後に開いた端末の印 (ほかの端末と同じページを開く連動を入れた端末だけが送る)。無ければ省略される。 */
  device_id?: string;
}

function path(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/plans/${encodeURIComponent(
    personId,
  )}/reading_positions`;
}

export async function fetchReadingPositions(
  familyId: string,
  personId: string,
  signal?: AbortSignal,
): Promise<StudyReadingPosition[]> {
  const res = await apiFetch<{ positions: StudyReadingPosition[] }>(
    path(familyId, personId),
    signal ? { signal } : {},
  );
  return res.positions;
}

/**
 * 1資料ぶんの閲覧位置を上書きする。
 *
 * sync を渡すと、端末の印を一緒に保存し、ほかの端末へ知らせる (連動している端末だけが渡す)。
 */
export async function putReadingPosition(
  familyId: string,
  personId: string,
  sourceId: string,
  page: number,
  opts?: { keepalive?: boolean; sync?: { deviceId: string } },
): Promise<StudyReadingPosition> {
  const body = opts?.sync ? { page, device_id: opts.sync.deviceId, notify: true } : { page };
  return apiFetch<StudyReadingPosition>(
    `${path(familyId, personId)}/${encodeURIComponent(sourceId)}`,
    { method: "PUT", body, ...(opts?.keepalive ? { keepalive: true } : {}) },
  );
}
